import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { BodyText, CaptionLink, RadioButton } from '@kvdbil/components';
import styled from 'styled-components';
import { FormData, FormField } from './DynamicForm';

const ScrollContainer = styled.div`
  overflow-x: auto;
`;

const LikertTable = styled.div`
  display: flex;
  flex-direction: column;
  min-width: min-content;
`;

const LikertRow = styled.div<{ $columns: number; $alternate?: boolean }>`
  display: grid;
  grid-template-columns: minmax(8rem, 1.5fr) repeat(
      ${({ $columns }) => $columns},
      minmax(5rem, 1fr)
    );
  column-gap: 1.5rem;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: ${({ theme, $alternate }) =>
    $alternate ? theme.colors.background.gray : 'transparent'};
`;

const HeaderCell = styled.div`
  text-align: center;
`;

const RadioCell = styled.div`
  display: flex;
  justify-content: center;
`;

type Props = {
  field: FormField;
  control: Control<FormData>;
};

const LikertField = ({ field, control }: Props) => {
  const {
    backendKey,
    required,
    likertLabel,
    likertScaleOptions,
    likertStatements
  } = field;
  const columnCount = likertScaleOptions?.length ?? 0;

  if (!likertScaleOptions?.length) return null;

  return (
    <div>
      <BodyText>{likertLabel}</BodyText>
      <ScrollContainer>
        <LikertTable>
          <LikertRow $columns={columnCount}>
            <span />
            {likertScaleOptions?.map((option, optionIndex) => {
              if (!option.label) return null;

              return (
                <HeaderCell key={`${option.label}_${optionIndex}`}>
                  <CaptionLink>{option.label}</CaptionLink>
                </HeaderCell>
              );
            })}
          </LikertRow>
          {likertStatements?.map((statement, index) => {
            const statementKey = statement.statementKey || `statement_${index}`;
            const fieldName = `${backendKey}.${statementKey}`;
            return (
              <Controller
                key={statementKey}
                name={fieldName}
                control={control}
                rules={{
                  validate: value => {
                    if (required) {
                      return Boolean(value);
                    }
                    return true;
                  }
                }}
                render={({ field: controllerField, fieldState }) => {
                  const handleScaleChange = (
                    e: React.ChangeEvent<HTMLInputElement>
                  ) => controllerField.onChange(e.target.value);
                  const statementLabel = statement.label ?? undefined;

                  return (
                    <LikertRow
                      role="radiogroup"
                      aria-label={statementLabel}
                      $columns={columnCount}
                      $alternate={index % 2 === 0}
                    >
                      <CaptionLink>{statement.label}</CaptionLink>
                      {likertScaleOptions?.map((option, optionIndex) => {
                        const { label } = option;
                        if (!label) return null;

                        return (
                          <RadioCell key={`${label}_${optionIndex}`}>
                            <RadioButton
                              aria-label={
                                statementLabel
                                  ? `${statementLabel}: ${label}`
                                  : label
                              }
                              name={fieldName}
                              value={label}
                              checked={controllerField.value === label}
                              color="info"
                              onChange={handleScaleChange}
                              hasError={Boolean(fieldState.error)}
                            />
                          </RadioCell>
                        );
                      })}
                    </LikertRow>
                  );
                }}
              />
            );
          })}
        </LikertTable>
      </ScrollContainer>
    </div>
  );
};

export default LikertField;
